'use client';

import cn from 'classnames';
import { forwardRef, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';

import { useAnalytics } from '@/hooks/useAnalytics';
import { useGtmDataLayer } from '@/hooks/useGtmDataLayer';
import { useSubmitForm } from '@/hooks/useSubmitForm';
import { useTryFormErrorsCheck } from '@/hooks/useTryFormErrorsCheck';
import { DataFromFormInputs } from '@/types';

import { Button } from '../Button';
import { Wrapper } from '../Wrapper';
import { Checkbox } from './Checkbox';
import s from './Form.module.scss';
import { Input } from './Input';
import { TextArea } from './TextArea';
import { Upload } from './Upload';

type ValidationType = 'name' | 'phone' | 'email' | 'default';

interface InputsDataType {
  label: string;
  placeholder: string;
  validationType: ValidationType;
  name: string;
  required: boolean;
  requiredText?: string;
  inputType?: 'phone';
  fieldName?: string;
}

const MockInputs: InputsDataType[] = [
  {
    label: 'Имя',
    placeholder: ' ',
    validationType: 'name',
    name: 'name',
    required: true,
  },
  {
    label: 'Телефон',
    placeholder: 'Введите ваш телефон',
    validationType: 'phone',
    name: 'phone',
    required: true,
    inputType: 'phone',
    fieldName: 'tel',
  },
  {
    label: 'Электронная почта',
    placeholder: ' ',
    validationType: 'email',
    name: 'email',
    required: true,
  },
];

const ErrorMessage = ({ statusCode }: { statusCode: string }) => {
  const { clickAnalytics } = useAnalytics();

  if (statusCode === '12022') {
    return (
      <p className={s.text}>
        Данные не отправлены. Некорректный тип файла. Пожалуйста, загрузите файл
        в другом формате.
      </p>
    );
  }

  return (
    <>
      <p className={s.text}>
        Данные не отправлены. Попробуйте отправить форму еще раз или напишите на{' '}
        <a
          onClick={() =>
            clickAnalytics({
              action: 'hr@cloud.ru',
              clickZone: 'body',
              clickElement: 'buttonclick',
              clickContent: 'hr@cloud.ru',
              uniqueId: `error-form-notice-mail`,
              transitionType: 'outside-link',
              clickZoneCategory: 'site-body',
              transitionTypeMetrics: 'external',
              transitionUrl: 'hr@cloud.ru',
            })
          }
          href="mailto:hr@cloud.ru"
          className="link"
        >
          hr@cloud.ru
        </a>
      </p>
    </>
  );
};

interface NoticeProps {
  onClick(): void;
  children: React.ReactNode;
}

const Notice = ({ onClick, children }: NoticeProps) => {
  return (
    <div className={s.notice}>
      {children}
      <Button
        tagElement="button"
        onClick={onClick}
        className={s.noticeButton}
        withSlashes
        analyticsOptions={{
          action: 'click',
          clickZone: 'body',
          clickElement: 'buttonclick',
          clickContent: 'Отправить форму снова',
          uniqueId: `form-Отправить форму снова`,
          transitionType: 'inside-link',
          clickZoneCategory: 'site-body',
        }}
      >
        Отправить форму снова
      </Button>
    </div>
  );
};

interface FormProps {
  children?: React.ReactNode;
  vacancy_id?: string;
  className?: string;
  internship?: boolean; // отношение к стажировке, тогда будет автореплай на почту
  successMessage?: string;
}

const Form = forwardRef<HTMLDivElement, FormProps>(
  (
    {
      children,
      vacancy_id = '3613580', // вакансия мешок для сбора стандартных данных пользователей
      className = '',
      internship = false,
      successMessage,
    },
    ref,
  ) => {
    const {
      register,
      handleSubmit,
      formState: { errors, isSubmitting, dirtyFields },
      setValue,
      reset,
      resetField,
    } = useForm<DataFromFormInputs>({
      mode: 'onChange',
    });
    const [uploadFileName, setUploadFileName] = useState('Приложить файл');
    const [isFormInteractionFirst, setIsFormInteractionFirst] = useState(true);

    const { submitForm, formStatus, setFormStatus, resultCode } =
      useSubmitForm();
    const { sendDataToDataLayer } = useGtmDataLayer();
    const { fillFormField, resumeSent, formInteractionSent } = useAnalytics();

    useTryFormErrorsCheck(errors, isSubmitting, 'career', 'career');

    const resetFields = () => {
      const fileList = new DataTransfer();

      setValue('phone', '');
      setValue('resume', fileList.files);
      setUploadFileName('Приложить файл');
      setValue('agreement', '');

      reset();
    };

    const onSubmit = async (data: FieldValues) => {
      const dataLayerToSend = {
        dataLayer: {
          event: 'custom_event',
          event_category: 'career LeadForm',
          event_action: 'formSuccess',
          event_label: vacancy_id ?? window.location.href,
        },
      };

      const formData = new FormData();

      for (const key in data) {
        if (key === 'resume') {
          formData.append('file', data.resume[0]);
        } else {
          formData.append(key, data[key]);
        }
      }

      formData.append('userAgreement', 'true');

      formData.append('vacancy_id', vacancy_id);
      if (internship) formData.append('internship', internship.toString()); // если это стажировка

      if (formData) {
        await submitForm({
          backendPatchInput: '/career/v1/applicant',
          formData: formData,
          formName: 'career',
          allFormSentFormType: 'career',
          onSuccess: () => {
            resumeSent();
            sendDataToDataLayer({
              ...dataLayerToSend,
            });

            resetFields();
          },
        });
      }
    };

    const onNoticeButtonClick = () => {
      if (formStatus === 'success') reset();
      setFormStatus('idle');
    };

    const defaultText = (
      <>
        Оставь контактные данные и резюме, <br />
        и мы вернемся к тебе, <br />
        когда появится <br />
        <span>подходящая роль!</span>
      </>
    );

    const defaultSuccessMessage = (
      <>
        Данные отправлены. Мы&nbsp;свяжемся с тобой, как только подберем
        подходящую вакансию
      </>
    );

    const agreementText = `Я даю <a target="_blank" href="https://cdn.cloud.ru/backend/docs/security/consent_applicant.pdf ">согласие</a> на обработку моих персональных данных в соответствии с условиями <a target="_blank" href="https://cdn.cloud.ru/backend/docs/security/politic.pdf">политики конфиденциальности</a>`;

    return (
      <section className={cn(s.root, className)} ref={ref}>
        <Wrapper className={s.wrapper}>
          <div className={s.content}>
            <h2 className={s.title}>откликнуться</h2>
            {(formStatus === 'idle' || formStatus === 'sending') && (
              <p className={s.text}>{children ?? defaultText}</p>
            )}
            {formStatus === 'success' && (
              <Notice onClick={onNoticeButtonClick}>
                <p className={s.text}>
                  {successMessage ?? defaultSuccessMessage}
                </p>
              </Notice>
            )}
            {formStatus === 'error' && (
              <Notice onClick={onNoticeButtonClick}>
                <ErrorMessage statusCode={resultCode} />
              </Notice>
            )}
          </div>
          <form
            onClick={() => {
              if (isFormInteractionFirst) {
                formInteractionSent({
                  formName: 'career',
                });
                setIsFormInteractionFirst(false);
              }
            }}
            className={cn(s.inputs, {
              [s.hidden]: formStatus !== 'idle' && formStatus !== 'sending',
            })}
            onSubmit={handleSubmit(onSubmit)}
          >
            {MockInputs.map(
              (
                {
                  label,
                  placeholder,
                  validationType,
                  required,
                  name,
                  inputType,
                  fieldName,
                },
                index,
              ) => {
                return (
                  <Input
                    key={index}
                    label={label}
                    validationType={validationType}
                    placeholder={placeholder}
                    name={name}
                    inputType={inputType}
                    errors={errors}
                    register={register}
                    dirtyFields={dirtyFields}
                    required={required}
                    data-qa="section_input"
                    onBlur={(event) => {
                      if (event.currentTarget.value) {
                        fillFormField({
                          formType: 'body',
                          fieldName: fieldName ?? name,
                        });
                      }
                    }}
                  />
                );
              },
            )}
            <Upload
              label={uploadFileName}
              changeFileName={(name: string) => setUploadFileName(name)}
              errors={errors}
              name="resume"
              register={register}
              accept=".doc,.docx,.pdf,.jpeg,.jpg,.png,.rtf,.zip"
            />

            <TextArea
              label="Комментарий"
              name="comment"
              required={false}
              errors={errors}
              register={register}
              resetField={resetField}
              className={s.textarea}
              data-qa="section_textarea"
              onBlur={(event) => {
                if (event.currentTarget.value) {
                  fillFormField({
                    formType: 'body',
                    fieldName: 'comment',
                  });
                }
              }}
            />
            <Button
              isLoading={formStatus === 'sending'}
              isDisabled={formStatus === 'sending'}
              tagElement="button"
              type="submit"
              withSlashes
              className={s.button}
              analyticsOptions={{
                action: 'click',
                clickZone: 'body',
                clickElement: 'buttonclick',
                clickContent: 'Оставить резюме',
                uniqueId: `form-Оставить резюме`,
                transitionType: 'inside-link',
                clickZoneCategory: 'site-body',
              }}
            >
              Оставить резюме
            </Button>
            <Checkbox
              label={agreementText}
              name="agreement"
              required
              errors={errors}
              register={register}
              data-qa="agreement"
            />
          </form>
        </Wrapper>
      </section>
    );
  },
);

Form.displayName = 'Form';

export { Form };
